import { defineNuxtPlugin, useNuxtApp, useRouter } from 'nuxt/app';
import { extractResourceFromPath } from '~~/common/utils/urlResource';
import { crossPlatformHashNotExceptionSafe, epsRound, isCategoryProductList } from '~~/common/utils';
import { getAnaliticsUnit, itemToQuantity, unitConverter } from '~~/common/utils/variant';
import { PM_ONSITE } from '~~/common/config/PaymentMethod';
import { CT_FOOD, CT_NONFOOD } from '~~/common/config/CheckoutTypes';
import { DM_DELIVERY_ADDRESS, DM_DELIVERY_CLICK_AND_COLLECT, DM_DELIVERY_COOLOMAT_LOCKER, DM_DELIVERY_FOXPOST_LOCKER, DM_DELIVERY_FOXPOST_PICKUP, DM_DELIVERY_GLS_LOCKER, DM_DELIVERY_GLS_PICKUP } from '~~/common/config/DeliveryMethod';
import { HASH_SHA_512 } from '~~/common/config/CrossPlatformHashFunction';
import { DEFAULT_CURRENCY } from '~~/common/config/LocaleConfig';
import { BILLING_METHOD_COMPANY, BILLING_METHOD_DELIVERY, BILLING_METHOD_PERSONAL } from '~~/common/config/BillingConfig';
import deliveryMethodsEnum from '~~/shop/app/components/Common/Modals/AwDeliveryMethodAndTimeframeModal/enum/deliveryMethodsEnum';
import { useUrlResourceStore } from '~~/common/stores/urlResource';
import type { CartSplit } from '~~/common/stores/order';
import { useOrderStore } from '~~/common/stores/order';
import { useProductStore } from '~~/common/stores/product';
import type { ProductData } from '~~/common/stores/product';
import { useSeoStore } from '~~/common/stores/seo';
import type { UserType } from '~~/common/stores/user';
import { useUserStore } from '~~/common/stores/user';
import { useAnalyticsQueueStore } from '~~/common/stores/analyticsQueue';
import { useAnalyticsProductListQueueStore } from '~~/common/stores/analyticsProductListQueue';
import { useCategoryStore } from '~~/common/stores/category';
import type { CheckoutData } from '~~/common/stores/checkout';
import { useCheckoutStore } from '~~/common/stores/checkout';
import { useDeliveryStore } from '~~/shop/app/stores/delivery';
import { usePromotionStore } from '~~/shop/app/stores/promotion';
import { calculatePageType } from '~~/common/config/RoutePageTypeMap';
import { AnalyticsBannerInteraction } from '~~/common/utils/AnalyticsBannerInteraction';
import type { BannerInteractionInput } from '~~/common/utils/AnalyticsBannerInteraction';
import { getTypeByTimeFrame, timeFrameTypes } from '~~/shop/app/components/Common/Modals/AwDeliveryMethodAndTimeframeModal/timeframe/timeFrameLengthHelper';
import type { DeepPartial, Schema } from '~~/common/types/api';

export const CLICK_MAP_TYPE_DEPARTMENT_STORE = Symbol('clickMapTypeDepartmentStore');
export const CLICK_MAP_TYPE_AW_SHOP_AREA = Symbol('clickMapTypeAwShopArea');
export const PCAT_ADD_PRODUCT = Symbol('pcatAddProduct');
export const PCAT_CANCEL = Symbol('pcatCancel');
export const PCAT_ACCEPT = Symbol('pcatAccept');
export const CDM_RES = Symbol('cdmRes');
export const CDM_POSTCODE = Symbol('cdmPostcode');
export const KS_MANUAL = Symbol('keywordSearchManual');
export const KS_LIST = Symbol('keywordSearchList');
export const KS_SUGGESTION = Symbol('keywordSearchSuggestion');
export const KS_CATEGORY = Symbol('keywordSearchCategory');
export const KS_PRODUCT = Symbol('keywordSearchProduct');
const LISTEN_FOR_SEO_CHANGE = Symbol('listenForSeoChange');
export const PUSH_LOCALE_CODE_CHANGE = Symbol('pushLocaleCodeChange');
export const PUSH_TRACK_PAGE_VIEW = Symbol('pushTrackPageView');
export const PUSH_KEYWORD_SEARCH = Symbol('pushKeywordSearch');
export const PUSH_PRODUCT_VIEW = Symbol('pushProductView');
export const PUSH_CATEGORY_VIEW = Symbol('pushCartegoryView');
const PUSH_CART_VIEW = Symbol('pushCartView');
export const PUSH_ADD_NEW_ITEM_TO_CART_PRE = Symbol('pushAddNewItemToCart');
export const PUSH_TRACK_CART_STATE = Symbol('pushTrackCartState');
export const PUSH_TRUNCATE_CART = Symbol('pushTruncateCart');
export const PUSH_REMOVE_CART_ITEM = Symbol('pushRemoveCartItem');
export const PUSH_ITEM_TO_FAVOURITE = Symbol('pushItemToFavourite');
export const PUSH_BASKET_TO_FAVOURITE = Symbol('pushBasketToFavourite');
export const PUSH_ADD_VOUCHER = Symbol('pushAddVoucher');
export const PUSH_ADD_TO_CART = Symbol('pushAddToCart');
export const PUSH_REMOVE_FROM_CART = Symbol('pushRemoveFromCart');
export const PUSH_CLICK_CATEGORY = Symbol('pushClickCategory');
export const PUSH_FILTER_PRODUCT = Symbol('pushFilterProduct');
export const PUSH_SELECT_ITEM = Symbol('pushSelectItem');
export const PUSH_CHANGE_CHECKOUT_DELIVERY_DATA = Symbol('pushChangeCheckoutDeliveryData');
export const PUSH_CHANGE_CHECKOUT_BILLING_DATA = Symbol('pushChangeCheckoutBillingData');
export const PUSH_BEGIN_CHECKOUT = Symbol('pushBeginCheckout');
export const PUSH_COMPARE_PRODUCT = Symbol('pushCompareProduct');
export const PUSH_CHANGE_CHECKOUT_PAYMENT_DATA = Symbol('pushChangeCheckoutPaymentMethod');
export const PUSH_CLICK_MAP = Symbol('pushClickMap');
export const PUSH_CHANGE_DELIVERY_METHOD = Symbol('pushChangeDeliveryMethod');
export const PUSH_PRE_ORDER = Symbol('pushPreOrder');
export const PUSH_POST_ORDER = Symbol('pushPostOrder');
export const PUSH_DELETE_ORDER = Symbol('pushDeleteOrder');
export const PUSH_VOUCHER = Symbol('pushVoucher');
export const PUSH_ZOOM_PRODUCT = Symbol('pushZoomProduct');
export const PUSH_CLICK_BANNER = Symbol('pushClickBanner');
export const PUSH_VIEW_BANNER = Symbol('pushViewBanner');
export const FLUSH_VIEW_BANNER = Symbol('flushViewBanner');
export const PUSH_REVIEW_PRODUCT = Symbol('pushReviewProduct');
export const PUSH_NEWSLETTER = Symbol('pushNewsletter');
export const PUSH_REGISTRATION_SUCCESS = Symbol('pushRegistrationSuccess');
export const PUSH_REGISTRATION_ERROR = Symbol('pushRegistrationError');
export const PUSH_LOGOUT = Symbol('pushLogout');
export const PUSH_LOGIN_SUCCESS = Symbol('pushLoginSuccess');
export const PUSH_LOGIN_ERROR = Symbol('pushLoginError');
export const PUSH_RANGE_FILTER = Symbol('pushRangeFilter');
const PUSH_PAGE_INIT_EVENT = Symbol('pushPageInitEvent');
export const PUSH_PURCHASE = Symbol('pushPurchase');
export const PUSH_VIEW_PRODUCT_LIST = Symbol('pushViewProductList');
export const FLUSH_VIEW_PRODUCT_LIST = Symbol('flushViewProductList');
export const PUSH_VIEW_CART = Symbol('pushViewCart');
export const PUSH_SELECT_HEADER_ITEM = Symbol('pushSelectHeaderItem');

export interface AnalyticsItem {
  promotion_id: string,
  promotion_name: string | null,
}

interface AnalyticsEvent {
  type: string,
  data: () => {
    event?: string,
    eventCat?: string,
    eventAct?: string,
    eventLbl?: string | number,
    term?: string,
    method?: string,
    number_of_products?: number,
    ecommerce?: {
      items?: AnalyticsItem[] | AnalyticsProduct[],
      purchase?: {
        actionField: {
          id: string,
          affiliation: string | undefined,
          revenue: string,
          tax: string,
          shipping: string,
          coupon: string,
        },
        products: {
          name: string,
          id: string,
          price: number,
          brand: string,
          category: string,
          variant: string,
          quantity: number,
          coupon: string,
        }[],
      },
    } | null,
    category?: string,
    basket_type?: string,
    cart_amount?: number,
    products_quantity?: number,
    products?: AnalyticsCartItem[],
    transactionId?: string,
    transactionAffiliation?: string,
    transactionTotal?: number,
    transactionTax?: string,
    transactionShipping?: string,
    transactionProducts?: {
      sku: string,
      name: string,
      category: string,
      price: number,
      quantity: number,
    }[],
    view_item?: {
      number_of_products: number,
      ecommerce: {
        items: AnalyticsItem[],
      },
    },
    category_page_view?: {
      category: string,
    },
    cart_page_view?: {
      basket_type: string,
      cart_amount: number,
      products_quantity: number,
      products: AnalyticsCartItem[],
    },
    order_confirmation?: {
      order_id: string,
      userID: string,
      products_quantity: number,
      products: AnalyticsOrderCartItem[],
    },
  }
}

export class AnalyticsProduct {
  item_name: string;
  item_id: string;
  price: number;
  item_brand: string;
  item_category: string;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  item_type: string | undefined;
  loyalty_discount: string;
  discount: number | null;
  quantity?: number;
  item_list_name?: string | null;
  search_term?: string | null;
  index?: number;
  item_list_id?: string | null;

  constructor ({
    product,
    variantProperty,
    checkoutType,
  }: {
    product: ProductData | DeepPartial<Schema<'CartProductItemView'>>,
    variantProperty: 'selectedVariant' | 'variant',
    checkoutType?: string,
  }) {
    const checkoutTypeMap: Record<string, string> = {
      [CT_FOOD]: 'food',
      [CT_NONFOOD]: 'non-food',
    };

    // set variant type by variantProperty
    const variant = variantProperty === 'selectedVariant'
      ? ('selectedVariant' in product ? product.selectedVariant : undefined)
      : ('variant' in product ? product.variant : undefined);

    this.item_name = variant?.name ?? '';
    this.item_id = variant?.sku ?? '';
    this.price = epsRound(variant?.price?.grossDiscounted ?? 0);

    // handle brandName: if we have Product-default, it has brandName property, and CartProductItemView doesn't have it
    const productBrandName = 'brandName' in product ? product.brandName : null;
    this.item_brand = productBrandName || variant?.brandName || '';

    // handle categories
    const categories = 'categories' in product && Array.isArray(product.categories)
      ? product.categories
      : [];
    this.item_category = categories.length > 0 && categories[0] && typeof categories[0] !== 'string' ? categories[0].name ?? '-' : '-';
    this.item_category2 = categories.length >= 1 && categories[1] && typeof categories[1] !== 'string' ? categories[1].name ?? '-' : '-';
    this.item_category3 = categories.length >= 2 && categories[2] && typeof categories[2] !== 'string' ? categories[2].name ?? '-' : '-';
    this.item_category4 = categories.length >= 3 && categories[3] && typeof categories[3] !== 'string' ? categories[3].name ?? '-' : '-';

    // handle isNonFood: only Product-default has isNonFood property
    const isNonFood = 'isNonFood' in product ? product.isNonFood : undefined;
    this.item_type = (checkoutTypeMap?.[checkoutType ?? '']
        || (isNonFood
          ? checkoutTypeMap[CT_NONFOOD]
          : (isNonFood === false
            ? checkoutTypeMap[CT_FOOD]
            : ''
          )
        )
    );
    this.loyalty_discount = variant?.isLoyaltyPriceValid ? 'yes' : 'no';
    this.discount = variant && variant.price?.gross === variant.price?.grossDiscounted
      ? null
      : epsRound((variant?.price?.gross ?? 0) - (variant?.price?.grossDiscounted ?? 0));
  }
}

class POJOProp {
  toJSON () {
    const o: Record<string, unknown> = {};
    for (const key in this) {
      if (typeof this[key] !== 'function') {
        o[key] = this[key];
      }
    }
    return o;
  }
}

class AnalyticsCartItem extends POJOProp {
  sku: string;
  name: string;
  type: string | undefined;
  label: string[];
  category?: string[];
  price: number;
  quantity: number;
  package: string;

  constructor ({
    checkoutType,
    cartItem,
  }: {
    checkoutType?: string;
    cartItem: DeepPartial<Schema<'CartProductItemView'>>
  }) {
    super();
    // `variant` and its sku/name/piecePrice are required on the cart item (the cart payload is DeepPartial,
    // so default the same way AnalyticsProduct does to keep the GTM payload fields concrete).
    this.sku = cartItem?.variant?.sku ?? '';
    this.name = cartItem?.variant?.name ?? '';
    this.type = checkoutType?.toLowerCase?.();
    // this.campaign = "Csokit vagy csalunk"; TODO Implement later when BE is ready
    this.label = cartItem?.variant?.flags?.map?.(f => f?.name ?? '') ?? [];
    this.category = cartItem?.categories?.map?.(c => c?.name || '');
    this.price = cartItem?.variant?.piecePrice?.grossDiscounted ?? 0;
    this.quantity = itemToQuantity<string>(cartItem);
    this.package = `${cartItem?.quantity?.quantity ?? 0} ${getAnaliticsUnit({ quantityType: cartItem?.quantity?.type })}`;
  }
}

class AnalyticsOrderCartItem extends AnalyticsCartItem {
  depot: string;

  constructor ({
    checkoutType,
    cartItem,
    shippingDepot,
  }: {
    checkoutType?: string;
    cartItem: DeepPartial<Schema<'CartProductItemView'>>;
    shippingDepot: string;
  }) {
    super({
      checkoutType,
      cartItem,
    });
    this.depot = shippingDepot;
  }
}

function getAnalyticsPricesFromSplit ({ split }: { split: DeepPartial<Schema<'CartSplitModel'>> | undefined }) {
  return {
    grand_total: split?.summary?.sum?.grossDiscounted,
    subtotal: split?.summary?.itemsSum?.gross,
    // tax: 970, NOTE: We do not have tax information
    shipping: (split?.summary?.fees?.delivery?.sum?.grossDiscounted ?? 0) - (split?.summary?.discounts?.delivery?.grossDiscounted ?? 0) || 0,
    discount: split?.summary?.discounts?.sum?.gross,
  };
}

// Computes the GA4 ecommerce `value` (sum of item.price * quantity) from a list
// of analytics items. quantity defaults to 1 for single-item events that don't
// carry a quantity. Returns a Number rounded to two decimals, as required by
// the GA4 ecommerce spec.
export function getAnalyticsValueFromItems ({ items }: { items: { price: number; quantity?: number }[] }) {
  return epsRound(
    items.reduce((sum, item) => {
      return sum + (Number.isFinite(item.price) ? item.price : 0) * (item.quantity ?? 1);
    }, 0),
  );
}

function getCartItemAndQuantityFromSplit ({ split }: { split: DeepPartial<Schema<'CartSplitModel'>> }) {
  return (split?.packages || [])
    .flatMap(p => p.items || [])
    .map(item => ({
      cartItem: item,
      quantity: itemToQuantity<string>(item),
    }))
  ;
}

function createPluginProvides () {
  const nuxtApp = useNuxtApp();
  const {
    $i18n,
    $logger,
    $api,
    $awGtm,
    $date,
    $pinia,
    $awMergedConfig,
    $getHref,
    $getHost,
    $awt,
  } = nuxtApp;
  const router = useRouter();
  const categoryStore = useCategoryStore($pinia);
  const promotionStore = usePromotionStore($pinia);
  const seoStore = useSeoStore($pinia);
  const orderStore = useOrderStore($pinia);
  const productStore = useProductStore($pinia);
  const deliveryStore = useDeliveryStore($pinia);
  const checkoutStore = useCheckoutStore($pinia);
  const urlResourceStore = useUrlResourceStore($pinia);
  const userStore = useUserStore($pinia);
  const analyticsQueueStore = useAnalyticsQueueStore($pinia);
  const analyticsProductListQueueStore = useAnalyticsProductListQueueStore($pinia);
  const T_GTM = 'trackerGtm';
  const T_TRACKERS = new Set([T_GTM]);
  let counter = 0;
  const awAnalytics = {
    [LISTEN_FOR_SEO_CHANGE] () {
      router.afterEach(() => {
        counter++;
        this[PUSH_TRACK_PAGE_VIEW]();
        if ($awMergedConfig.googleTagManagerId && $awMergedConfig.isGtmTrackingEnabled && counter > 1) {
          this[PUSH_PAGE_INIT_EVENT]();
        }
      });
    },
    _push (...analytics: AnalyticsEvent[]) {
      try {
        if (import.meta.client && $awMergedConfig.googleTagManagerId && $awMergedConfig.isGtmTrackingEnabled) {
          if (window.awIsGtmLoaded === false) {
            window.dataLayer = [];
          } else if (window.awIsGtmLoaded === true) {
            let idx;
            window.dataLayer.some((v, i) => {
              // Gets first element of not 0 indexed array.
              // https://stackoverflow.com/questions/37990756/get-first-element-in-array-with-index-not-starting-from-0#answer-37991354
              idx = i;
              return true;
            });
            if (window.dataLayer.length - (idx ?? 0) > 2 * $awMergedConfig.gtmDataLayerItemRetention) {
              for (const i in window.dataLayer) {
                if ((window.dataLayer.length - parseInt(i ?? 0)) > $awMergedConfig.gtmDataLayerItemRetention) {
                  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                  delete window.dataLayer[i];
                } else {
                  break;
                }
              }
            }
          }
        }
      } catch (err) {
        $logger.error(err);
      }
      analytics.forEach(function () {
        try {
          const {
            type,
            data,
          } = arguments[0];
          const dataResult = data();
          if (type === T_GTM) {
            if ($awMergedConfig.googleTagManagerId && $awMergedConfig.isGtmTrackingEnabled) {
              if ((typeof window === 'undefined' || window?.awIsGtmLoaded !== false)) {
                $awGtm.push(dataResult);
              }
            }
          } else if (T_TRACKERS.has(type)) {
            $logger.error(`type ${type} is in trackers ${[...T_TRACKERS]}, but it's unspecified how it should be handled.`);
          } else {
            $logger.error(`type should be ${[...T_TRACKERS]} it is: ${type}.`);
          }
        } catch (err) {
          $logger.error(err);
        }
      });
    },
    [PUSH_PAGE_INIT_EVENT] () {
      const oldBlock = document.getElementById('data-layer-page-init-script');
      if (oldBlock) {
        oldBlock.remove();
      }
      const personalInfo = userStore.data?.personalInfo;
      const pageType = calculatePageType(window.location.pathname, urlResourceStore.data);
      const pageTitle = document?.title || false;
      const pageLanguage = $i18n.locale.value.toUpperCase();
      const userId = personalInfo?.cdExternalId;
      const userLoggedIn = userStore.isEcomUser ? 'yes' : 'no';

      const newScriptBlock = document.createElement('script');
      newScriptBlock.id = 'data-layer-page-init-script';
      const pageInitData: {
        event: string;
        page_type: string;
        page_name: string | boolean;
        page_language: string;
        user_logged: string;
        user_id?: string;
      } = {
        event: 'pageInit',
        page_type: pageType,
        page_name: pageTitle,
        page_language: pageLanguage,
        user_logged: userLoggedIn,
      };
      if (userId) {
        pageInitData.user_id = userId;
      }
      newScriptBlock.innerHTML = `window.dataLayer.push(${JSON.stringify(pageInitData)});`;
      const defaultGTMScriptBlock = document.getElementById('gtm-default-script');
      document.head.insertBefore(newScriptBlock, defaultGTMScriptBlock);
    },
    [PUSH_LOCALE_CODE_CHANGE] ({ localeCode }: { localeCode: string }) {
      try {
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'eventTracker',
            eventCat: 'Nyelvválasztás',
            eventAct: localeCode?.toUpperCase?.(),
            eventLbl: $getHref(),
          }),
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_KEYWORD_SEARCH] ({
      analiticsType,
      searchKeyword,
    }: {
      analiticsType: string;
      searchKeyword: string;
    }) {
      try {
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'search',
            term: searchKeyword,
            method: analiticsType === KS_SUGGESTION.toString() ? 'suggest' : 'typing',
          }),
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    async [PUSH_PRODUCT_VIEW] ({ sku }: { sku: string }) {
      try {
        if ($awMergedConfig.googleTagManagerId && $awMergedConfig.isGtmTrackingEnabled) {
          const productData = productStore?.data;
          const product = productData?.selectedVariant?.sku === sku
            ? productData
            : await productStore.fetchMainProductForProductPage({
              loc: router.currentRoute.value.query.loc,
              identifierType: 'sku',
              id: sku,
            });
          if (!product) {
            return;
          }
          const item = new AnalyticsProduct({
            product,
            variantProperty: 'selectedVariant',
          });
          this._push({
            type: T_GTM,
            data: () => ({
              event: 'cleanup',
              ecommerce: null,
            }),
          });
          this._push({
            type: T_GTM,
            data: () => ({
              event: 'view_item',
              number_of_products: 1,
              ecommerce: {
                currency: DEFAULT_CURRENCY,
                value: getAnalyticsValueFromItems({ items: [item] }),
                items: [item],
              },
            }),
          });
        }
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_CATEGORY_VIEW] ({ categoryName }: { categoryName: string }) {
      try {
        if ($awMergedConfig.googleTagManagerId && $awMergedConfig.isGtmTrackingEnabled) {
          this._push({
            type: T_GTM,
            data: () => ({
              event: 'category_page_view',
              // this.campaign = "Csokit vagy csalunk"; TODO Implement later when BE is ready
              category: categoryName,
            }),
          });
        }
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_CART_VIEW] () {
      try {
        if ($awMergedConfig.googleTagManagerId && $awMergedConfig.isGtmTrackingEnabled) {
          const cartItems = (orderStore.order.splits || []).flatMap((s: DeepPartial<Schema<'CartSplitModel'>>) => {
            return (s.packages || []).flatMap(p => p.items || []).map(item => ({
              checkoutType: s.checkoutType,
              cartItem: item,
              quantity: itemToQuantity<string>(item),
            }));
          });
          const splits: DeepPartial<Schema<'CartSplitModel'>>[] = orderStore.order?.splits || [];
          this._push({
            type: T_GTM,
            data: () => ({
              event: 'cart_page_view',
              basket_type: (splits?.length > 1 ? 'mixed' : splits?.[0]?.checkoutType)?.toLowerCase?.(),
              cart_amount: splits.reduce((a, b) => {
                return a + (b.summary?.sum?.grossDiscounted ?? 0);
              }, 0),
              products_quantity: cartItems.reduce((a, i) => a + (i.quantity || 0), 0),
              products: cartItems.map(({
                checkoutType,
                cartItem,
              }) => {
                try {
                  return new AnalyticsCartItem({
                    checkoutType,
                    cartItem,
                  });
                } catch (err) {
                  $logger.error(err);
                  return null;
                }
              }).filter((item: AnalyticsCartItem | null): item is AnalyticsCartItem => item !== null),
            }),
          });
        }
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_TRACK_PAGE_VIEW] () {
      try {
        if (!($awMergedConfig.googleTagManagerId && $awMergedConfig.isGtmTrackingEnabled)) {
          return;
        }
        Promise.race([
          new Promise(resolve => setTimeout(() => resolve('timeout'), 1000)),
          Promise.allSettled([seoStore.getSeoReadonlyRequestsEnRoute()]),
        ]).finally(() => {
          const newTitle = document?.title || '';
          const newHref = $getHref();

          try {
            const urlResource: { resourceType: string, resourceId: string } = extractResourceFromPath(newHref) || { resourceType: '', resourceId: '' };
            if (urlResource?.resourceType === 'product') {
              this[PUSH_PRODUCT_VIEW]({ sku: urlResource.resourceId });
            } else if (urlResource.resourceType === 'category') {
              const currentCat = categoryStore.getCategoryFromTree;
              const currentCatId = parseInt(String(currentCat?.id), 10);
              const searchedCatId = parseInt(urlResource.resourceId, 10);
              const searchedCat = currentCatId === searchedCatId ? currentCat : categoryStore.getCategoryFromTreeById({ treeId: searchedCatId });
              if (!isCategoryProductList(searchedCat ?? undefined)) {
                this[PUSH_CATEGORY_VIEW]({ categoryName: newTitle });
              }
            } else if (router.currentRoute.value.name?.toString?.()?.startsWith('shop-basket___')) {
              this[PUSH_CART_VIEW]();
            }
          } catch (err) {
            $logger.error(err);
          }
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    async [PUSH_ADD_NEW_ITEM_TO_CART_PRE] ({ objectType }: { objectType: string }) {
      try {
        if ($awMergedConfig.googleTagManagerId && $awMergedConfig.isGtmTrackingEnabled) {
          switch (objectType) {
            case 'variant': {
              // NOTE: For perf reasons does not update cart for
              // objectType variant.
              break;
            }
            case 'order':
            case 'shoppingList': {
              // NOTE: Updates cart before analitics calculates the
              // changes between old vs new cart.
              await orderStore.fetchOrder({
                id: 'current',
                canStartSecondAction: true,
                canRunSimultaneous: true,
              });
              break;
            }
          }
        }
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_TRACK_CART_STATE] ({
      newCart,
      oldCart,
      newObject,
      modifiedObject,
    }: {
      newCart: DeepPartial<Schema<'CartModel'>>;
      oldCart: DeepPartial<Schema<'CartModel'>>;
      newObject?: {
        objectType: string;
        objectId: string;
      };
      modifiedObject?: {
        uuid: string
      };
    }) {
      try {
        if (!($awMergedConfig.googleTagManagerId && $awMergedConfig.isGtmTrackingEnabled)) {
          return;
        }
        const [oldCartItems, newCartItems] = [oldCart, newCart]
          .map((order) => {
            return (order.splits || []).flatMap((s) => {
              return (s.packages || []).flatMap(p => p.items || []).map(item => ({
                checkoutType: s.checkoutType,
                cartItem: item,
                quantity: itemToQuantity<string>(item),
              }));
            });
          })
          .map(flatO => Object.fromEntries(flatO.map((i): [string, typeof i] => [String(i.cartItem.uuid), i]))) as [
          Record<string, { checkoutType?: string; cartItem: DeepPartial<Schema<'CartProductItemView'>>; quantity: number }>,
          Record<string, { checkoutType?: string; cartItem: DeepPartial<Schema<'CartProductItemView'>>; quantity: number }>,
        ]
        ;
        const uniqueUuids = [...new Set([oldCartItems, newCartItems].flatMap(o => Object.keys(o)))];
        const removedUuids = uniqueUuids.filter((uuid) => {
          return newCartItems[uuid]?.quantity !== oldCartItems[uuid]?.quantity && oldCartItems[uuid]?.quantity;
        });
        const addedUuids = uniqueUuids.filter((uuid) => {
          return newCartItems[uuid]?.quantity !== oldCartItems[uuid]?.quantity && newCartItems[uuid]?.quantity;
        });
        if (removedUuids.length + addedUuids.length) {
          const newCartItemsValues = Object.values(newCartItems);
          try {
            if (newObject) {
              const newlyAddedCartItems = ['order', 'shoppingList'].includes(newObject.objectType)
                ? addedUuids.filter((uuid) => {
                  return !oldCartItems[uuid]?.quantity;
                }).map((uuid) => {
                  return newCartItems[uuid];
                }).filter((item): item is { checkoutType?: string; cartItem: DeepPartial<Schema<'CartProductItemView'>>; quantity: number } => !!item)
                : newCartItemsValues.filter(({ cartItem }) => {
                  return newObject.objectId.toString() === cartItem.variant?.id?.toString();
                })
              ;
              if (newlyAddedCartItems.length) {
                this._push({
                  type: T_GTM,
                  data: () => ({
                    event: 'added_to_cart',
                    'saved basket': newObject?.objectType === 'shoppingList' ? 'yes' : 'no',
                    products: newlyAddedCartItems.map((item) => {
                      const { checkoutType, cartItem } = item;
                      try {
                        return new AnalyticsCartItem({
                          checkoutType,
                          cartItem,
                        });
                      } catch (err) {
                        $logger.error(err);
                        return null;
                      }
                    }).filter((item: AnalyticsCartItem | null): item is AnalyticsCartItem => item !== null),
                  }),
                });
              }
            }
            if (['order', 'shoppingList'].includes(newObject?.objectType || '') || modifiedObject) {
              const modifiedCartItems = (newObject ? addedUuids : [modifiedObject?.uuid])
                .filter((uuid): uuid is string => typeof uuid === 'string' && !!oldCartItems[uuid]?.quantity)
                .map((uuid) => {
                  return newCartItems[uuid];
                })
                .filter((item): item is { checkoutType?: string; cartItem: DeepPartial<Schema<'CartProductItemView'>>; quantity: number } => !!item)
              ;
              modifiedCartItems.forEach(({
                checkoutType,
                cartItem,
              }) => {
                const oldCi = cartItem.uuid ? oldCartItems[cartItem.uuid]?.cartItem : undefined;
                if (!oldCi || oldCi.quantity?.quantity === undefined || oldCi.quantity?.type === undefined) {
                  return;
                }
                const newUnit = getAnaliticsUnit({ quantityType: cartItem?.quantity?.type });
                const oldQuantity = unitConverter<string>({
                  from: {
                    value: oldCi.quantity.quantity,
                    unit: oldCi.quantity.type,
                  },
                  to: newUnit,
                  weightPerPiece: cartItem.variant?.loose?.weightPerPiece ?? 0,
                });
                this._push({
                  type: T_GTM,
                  data: () => ({
                    event: 'product_quantity_change',
                    name: cartItem?.variant?.name,
                    type: checkoutType?.toLowerCase?.(),
                    change: itemToQuantity<string>(cartItem) - itemToQuantity<string>(oldCi),
                    package: `${(cartItem?.quantity?.quantity ?? 0) - oldQuantity.value} ${newUnit}`,
                  }),
                });
              });
            }
          } catch (err) {
            $logger.error(err);
          }
          const cartPrice = Object.values(newCartItems)
            .filter((item): item is { checkoutType?: string; cartItem: DeepPartial<Schema<'CartProductItemView'>>; quantity: number } => !!item)
            .reduce((a, {
              quantity,
              cartItem: { variant },
            }) => {
              return a + epsRound((variant?.piecePrice?.grossDiscounted || 0) * (quantity || 0));
            }, 0);
          this._push({
            type: T_GTM,
            data: () => {
              return {
                event: 'cart_update',
                cart_amount: cartPrice,
                products_quantity: newCartItemsValues.reduce((a, i) => a + (i.quantity || 0), 0),
                products: newCartItemsValues.map(({
                  checkoutType,
                  cartItem,
                }) => {
                  try {
                    return new AnalyticsCartItem({
                      checkoutType,
                      cartItem,
                    });
                  } catch (err) {
                    $logger.error(err);
                    return null;
                  }
                }).filter((item: AnalyticsCartItem | null): item is AnalyticsCartItem => item !== null),
              };
            },
          });
        }
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_REMOVE_CART_ITEM] ({
      uuid,
      newQuantity,
    }: {
      uuid: string;
      newQuantity: {
        value: number;
        unit: string;
      };
    }) {
      try {
        if ($awMergedConfig.googleTagManagerId && $awMergedConfig.isGtmTrackingEnabled) {
          let cartItem!: DeepPartial<Schema<'CartProductItemView'>>;
          let checkoutType;
          const splits: DeepPartial<Schema<'CartSplitModel'>>[] = orderStore.order.splits || [];
          if (splits?.some?.((split) => {
            const foundItem = (split?.packages || []).flatMap(p => p.items || []).find(i => i.uuid === uuid);
            if (foundItem) {
              cartItem = foundItem;
              checkoutType = split.checkoutType;
            }
            return Boolean(foundItem);
          })) {
            const item = new AnalyticsProduct({
              product: cartItem,
              variantProperty: 'variant',
              checkoutType,
            });
            const newConvertedQuantity = unitConverter<string>({
              from: {
                value: newQuantity?.value || 0,
                unit: newQuantity?.unit || 'piece',
              },
              to: 'piece',
              weightPerPiece: cartItem.variant?.loose?.weightPerPiece ?? 0,
            }).value;
            const itemQuantity = itemToQuantity<string>(cartItem);
            item.quantity = itemQuantity - newConvertedQuantity;
            return () => {
              this._push({
                type: T_GTM,
                data: () => ({
                  event: 'cleanup',
                  ecommerce: null,
                }),
              });
              this._push({
                type: T_GTM,
                data: () => ({
                  event: 'remove_from_cart',
                  number_of_products: 1,
                  ecommerce: {
                    items: [item],
                  },
                }),
              });
            };
          }
        }
      } catch (err) {
        $logger.error(err);
      }
      return () => {
      };
    },
    [PUSH_ITEM_TO_FAVOURITE] ({
      productData,
      variantProperty,
    }: {
      productData: ProductData | Schema<'CartProductItemView'>;
      variantProperty: 'selectedVariant' | 'variant';
    }) {
      try {
        const item = new AnalyticsProduct({
          product: productData,
          variantProperty,
        });
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'cleanup',
            ecommerce: null,
          }),
        });
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'add_to_wishlist',
            number_of_products: 1,
            ecommerce: {
              items: [item],
            },
          }),
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_BASKET_TO_FAVOURITE] ({ checkoutType }: { checkoutType: string }) {
      try {
        this._push({
          type: T_GTM,
          data: () => {
            const splits: DeepPartial<Schema<'CartSplitModel'>>[] = orderStore.order.splits || [];
            const split = splits?.find?.(s => s.checkoutType === checkoutType);
            const cartItems = split ? getCartItemAndQuantityFromSplit({ split }) : [];
            return {
              event: 'save_cart',
              save_type: 'Kedvencek',
              basket_type: checkoutType?.toLowerCase?.(),
              cart_amount: split?.summary?.sum?.grossDiscounted,
              products_quantity: cartItems.reduce((a, i) => a + (i.quantity || 0), 0),
              products: cartItems.map(({ cartItem }) => {
                try {
                  return new AnalyticsCartItem({
                    checkoutType,
                    cartItem,
                  });
                } catch (err) {
                  $logger.error(err);
                  return null;
                }
              }).filter((item: AnalyticsCartItem | null): item is AnalyticsCartItem => item !== null),
            };
          },
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_TRUNCATE_CART] ({ checkoutType }: { checkoutType: string }) {
      try {
        this._push({
          type: T_GTM,
          data: () => {
            const splits: DeepPartial<Schema<'CartSplitModel'>>[] = orderStore.order.splits || [];
            const split = splits?.find?.(s => s.checkoutType === checkoutType);
            const cartItems = split ? getCartItemAndQuantityFromSplit({ split }) : [];
            return {
              event: 'clear_cart',
              basket_type: checkoutType?.toLowerCase?.(),
              cart_amount: split?.summary?.sum?.grossDiscounted,
              products_quantity: cartItems.reduce((a, i) => a + (i.quantity || 0), 0),
              products: cartItems.map(({ cartItem }) => {
                try {
                  return new AnalyticsCartItem({
                    checkoutType,
                    cartItem,
                  });
                } catch (err) {
                  $logger.error(err);
                  return null;
                }
              }).filter((item: AnalyticsCartItem | null): item is AnalyticsCartItem => item !== null),
            };
          },
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_ADD_VOUCHER] ({ objectId }: { objectId: string }) {
      try {
        this._push({
          type: T_GTM,
          data: () => {
            const vouchers = orderStore.getCurrentFlattenedVouchers
              .filter(voucher => voucher.objectId === objectId)
            ;
            return {
              event: 'discount_added',
              discount_name: vouchers[0]?.name,
              discount: vouchers.reduce((a, b) => a + (b?.appliedAmount?.gross || 0), 0),
            };
          },
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_ADD_TO_CART] ({
      productData,
      productList,
      quantityData,
    }: {
      productData: ProductData;
      productList: string;
      quantityData: {
        quantity: number;
        type: string;
      };
    }) {
      try {
        const item = new AnalyticsProduct({
          product: productData,
          variantProperty: 'selectedVariant',
        });
        const quantity = unitConverter<string>({
          from: {
            value: quantityData?.quantity,
            unit: quantityData?.type,
          },
          to: 'piece',
          weightPerPiece: productData?.selectedVariant?.loose?.weightPerPiece || 0,
        })?.value;
        item.quantity = Math.round(quantity);
        item.item_list_name = productList;
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'cleanup',
            ecommerce: null,
          }),
        });
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'add_to_cart',
            number_of_products: 1,
            ecommerce: {
              currency: DEFAULT_CURRENCY,
              value: getAnalyticsValueFromItems({ items: [item] }),
              items: [item],
            },
          }),
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_REMOVE_FROM_CART] ({ name }: { name: string }) {
      this._push({
        type: T_GTM,
        data: () => ({
          event: 'eventTracker',
          eventCat: 'changeCart',
          eventAct: 'törlés',
          eventLbl: name,
        }),
      });
    },
    [PUSH_CLICK_CATEGORY] ({ parentsNameArr }: { parentsNameArr: { name: string }[] }) {
      this._push({
        type: T_GTM,
        data: () => ({
          event: 'eventTracker',
          eventCat: 'Kategória választás',
          eventAct: parentsNameArr?.[0]?.name,
          eventLbl: parentsNameArr?.slice(-1)?.[0]?.name,
        }),
      });
    },
    [PUSH_FILTER_PRODUCT] ({
      key,
      value,
    }: {
      key: string;
      value: string;
    }) {
      try {
        const shortListName = promotionStore.getShortlistByUrl(router.currentRoute.value.path)?.title;
        const categoryName = categoryStore.getCategoryFromTree?.name;
        const routeName = router.currentRoute.value.name;
        const loyaltyOffersName = (typeof routeName === 'string' && routeName.includes('shop-loyalty-offers___')) && $awt('aw.loyalty_offer.title');
        const searchListName = router.currentRoute.value.path.includes('search') && 'Search';
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'item_list_filtering',
            item_list_name: categoryName || shortListName || loyaltyOffersName || searchListName,
            filter_name: key,
            filter_value: value,
          }),
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_CHANGE_CHECKOUT_PAYMENT_DATA] () {
      try {
        const order = orderStore.order;
        const checkout = checkoutStore.checkout;
        const items = (order.splits || []).flatMap((s) => {
          return (s.packages || []).flatMap(p => p.items || []).map((item) => {
            const newItem = new AnalyticsProduct({
              product: item,
              variantProperty: 'variant',
              checkoutType: s.checkoutType,
            });
            newItem.quantity = itemToQuantity<string>(item);
            return newItem;
          });
        });
        this._push({
          type: T_GTM,
          data: () => {
            return {
              event: 'cleanup',
              ecommerce: null,
            };
          },
        });
        this._push({
          type: T_GTM,
          data: () => {
            return {
              event: 'add_payment_info',
              number_of_products: items.length,
              ecommerce: {
                payment_type: checkout?.payment?.type === PM_ONSITE ? 'on-delivery' : 'card',
                items,
              },
            };
          },
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_CHANGE_CHECKOUT_BILLING_DATA] ({ billingMethod }: { billingMethod: string }) {
      this._push({
        type: T_GTM,
        data: () => ({
          event: 'billing_information_added',
          billing_type: {
            [BILLING_METHOD_COMPANY]: 'Céges számlázási adat',
            [BILLING_METHOD_PERSONAL]: 'Lakossági számlázási adat',
            [BILLING_METHOD_DELIVERY]: 'Lakossági számlázási adat',
          }[billingMethod],
        }),
      });
    },
    [PUSH_COMPARE_PRODUCT] ({
      productCompareAnalyticsType,
      productsArr,
    }: {
      productCompareAnalyticsType: symbol;
      productsArr: Array<{ selectedVariant?: { sku?: string } | null } | undefined>;
    }) {
      if (productCompareAnalyticsType === PCAT_ACCEPT) {
        this._push({
          type: T_GTM,
          data: () => {
            return {
              event: 'product_comparing',
              item_id: productsArr?.[0]?.selectedVariant?.sku,
              item_id2: productsArr?.[1]?.selectedVariant?.sku,
              item_id3: productsArr?.[2]?.selectedVariant?.sku,
              item_id4: productsArr?.[3]?.selectedVariant?.sku,
            };
          },
        });
      }
    },
    [PUSH_SELECT_ITEM] ({
      productData,
      searchText,
      index,
      itemListId,
      itemListName,
    }: {
      productData: ProductData | Schema<'CartProductItemView'>;
      searchText: string[];
      index: number;
      itemListId?: string;
      itemListName?: string;
    }) {
      try {
        const item = new AnalyticsProduct({
          product: productData,
          variantProperty: 'selectedVariant',
        });
        const searchListName = router.currentRoute.value.path.includes('search') && 'Search';
        item.item_list_name = itemListName || searchListName || null;
        item.item_list_id = itemListId || undefined;
        item.search_term = searchText?.join(',') || null;
        item.index = index;
        this._push({
          type: T_GTM,
          data: () => {
            return {
              event: 'cleanup',
              ecommerce: null,
            };
          },
        });
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'select_item',
            number_of_products: 1,
            ecommerce: {
              currency: DEFAULT_CURRENCY,
              value: getAnalyticsValueFromItems({ items: [item] }),
              items: [item],
            },
          }),
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_CHANGE_CHECKOUT_DELIVERY_DATA] () {
      try {
        const order = orderStore.order;
        const checkout = checkoutStore.checkout;
        const department = (checkout?.delivery?.deliveryMethod === DM_DELIVERY_CLICK_AND_COLLECT
          ? checkout?.delivery?.departmentStore?.name
          : null
        );
        const shippingTier = [
          {
            name: 'delivery',
            methods: [DM_DELIVERY_ADDRESS],
          },
          {
            name: 'pickup',
            methods: [DM_DELIVERY_CLICK_AND_COLLECT],
          },
          {
            name: 'currier',
            methods: [
              DM_DELIVERY_GLS_PICKUP,
              DM_DELIVERY_GLS_LOCKER,
              DM_DELIVERY_FOXPOST_PICKUP,
              DM_DELIVERY_FOXPOST_LOCKER,
              DM_DELIVERY_COOLOMAT_LOCKER,
            ],
          },
        ].find(tier => tier.methods.includes(checkout?.delivery?.deliveryMethod ?? ''))?.name;
        const items = (order.splits || []).flatMap((s) => {
          return (s.packages || []).flatMap(p => p.items || []).map((item) => {
            const newItem = new AnalyticsProduct({
              product: item,
              variantProperty: 'variant',
              checkoutType: s.checkoutType,
            });
            newItem.quantity = itemToQuantity<string>(item);
            return newItem;
          });
        });
        const formatReservation = (reservation: DeepPartial<Schema<'CheckoutReservationModel'>> | null | undefined) => {
          if (!reservation) {
            return;
          }
          const type = getTypeByTimeFrame(reservation);
          if (type === timeFrameTypes.FULL_DAY) {
            return reservation.from?.split('T')[0];
          }
          return reservation.from;
        };
        this._push({
          type: T_GTM,
          data: () => {
            return {
              event: 'cleanup',
              ecommerce: null,
            };
          },
        });
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'add_shipping_info',
            number_of_products: items.length,
            delivery_datetime: shippingTier === 'delivery' ? formatReservation(checkout?.reservation) : undefined,
            department,
            ecommerce: {
              shipping_tier: shippingTier,
              items,
            },
          }),
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_BEGIN_CHECKOUT] ({ split }: { split: CartSplit }) {
      try {
        const items = (split.packages || []).flatMap(p => p.items || []).map((item) => {
          const newItem = new AnalyticsProduct({
            product: item,
            variantProperty: 'variant',
            checkoutType: split.checkoutType,
          });
          newItem.quantity = itemToQuantity<string>(item);
          return newItem;
        });
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'cleanup',
            ecommerce: null,
          }),
        });
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'begin_checkout',
            number_of_products: items.length,
            ecommerce: {
              currency: split?.summary?.sum?.currency || DEFAULT_CURRENCY,
              value: epsRound(split?.summary?.sum?.grossDiscounted ?? 0),
              items,
            },
          }),
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_CLICK_MAP] ({
      clickType,
      mapAreas,
    }: {
      clickType: typeof CLICK_MAP_TYPE_DEPARTMENT_STORE | typeof CLICK_MAP_TYPE_AW_SHOP_AREA;
      mapAreas: string[];
    }) {
      this._push({
        type: T_GTM,
        data: () => ({
          event: 'eventTracker',
          eventCat: 'Térkép',
          eventAct: {
            [CLICK_MAP_TYPE_DEPARTMENT_STORE]: 'Auchan áruházak, ahol átveheted rendelésed',
            [CLICK_MAP_TYPE_AW_SHOP_AREA]: 'Területek, ahová kiszállítjuk rendelésed',
          }[clickType],
          eventLbl: mapAreas.join(' - '),
        }),
      });
    },
    [PUSH_CHANGE_DELIVERY_METHOD] ({
      deliveryMethodId,
      checkoutType,
      changeTypes,
    }: {
      deliveryMethodId: string;
      checkoutType: string;
      changeTypes: (symbol | string)[];
    }) {
      try {
        if ($awMergedConfig.googleTagManagerId && $awMergedConfig.isGtmTrackingEnabled) {
          const receiptType = {
            [DM_DELIVERY_CLICK_AND_COLLECT]: 'Áruházban',
            [DM_DELIVERY_ADDRESS]: 'Házhozszállítással',
            [DM_DELIVERY_GLS_PICKUP]: DM_DELIVERY_GLS_PICKUP,
            [DM_DELIVERY_GLS_LOCKER]: DM_DELIVERY_GLS_LOCKER,
            [DM_DELIVERY_FOXPOST_PICKUP]: DM_DELIVERY_FOXPOST_PICKUP,
            [DM_DELIVERY_FOXPOST_LOCKER]: DM_DELIVERY_FOXPOST_LOCKER,
            [DM_DELIVERY_COOLOMAT_LOCKER]: DM_DELIVERY_COOLOMAT_LOCKER,
          }[deliveryMethodId];
          if (receiptType) {
            // The callers are not always on the checkout page, so the store's own
            // `shopCheckoutType` may still hold whatever the page last set (it defaults to FOOD and
            // is never reset). The event has to describe the checkout the caller acted on, so the
            // type travels as a parameter.
            const checkout = checkoutType.toLowerCase() === CT_NONFOOD.toLowerCase()
              ? checkoutStore.getNonfoodCheckout
              : checkoutStore.getFoodCheckout;
            let destination: string | undefined;
            switch (deliveryMethodId) {
              case DM_DELIVERY_GLS_PICKUP:
              case DM_DELIVERY_GLS_LOCKER:
              case DM_DELIVERY_FOXPOST_PICKUP:
              case DM_DELIVERY_FOXPOST_LOCKER:
              case DM_DELIVERY_COOLOMAT_LOCKER: {
                const pickupPointData = checkout?.delivery?.pickupPointData;
                if (pickupPointData) {
                  // address starting with zipcode
                  destination = pickupPointData.postCode;
                  destination += ' ' + pickupPointData.city;
                  destination += ' ' + pickupPointData.addressLine;
                  // something unique
                  destination += `||id: ${receiptType}/${pickupPointData.id}`;
                  // readable name
                  destination += `||name: ${pickupPointData.name}`;
                }
                break;
              }
              default: {
                const deliveryStoreSetup = deliveryStore?.setup;
                destination =/* checkout?.nonfoodReservations?.postCode || todo find which nonfoodReservations must use  */ checkout?.delivery?.departmentStore?.name || checkout?.delivery?.address?.address?.postCode || (deliveryStoreSetup?.type === 'department_store' ? deliveryStoreSetup?.name : deliveryStoreSetup?.postCode);
                if (destination === '9999') {
                  destination = undefined;
                }
                break;
              }
            }
            let deliveryTime: string | undefined;
            try {
              const timeFrame = checkout?.reservation;
              if (timeFrame?.from && timeFrame?.to) {
                const s0 = new Date(timeFrame.from);
                const s1 = new Date(timeFrame.to);
                const dateStr = $date(s0, { weekday: 'long' });
                const [f = '', ...o] = dateStr.split('');
                deliveryTime = `${f.toUpperCase() + o.join('')} - ${[s0, s1].map(s => [s.getHours(), s.getMinutes()].map(d => d.toString().padStart(2, '0')).join(':')).join('-')}`;
              }
            } catch (err) {
              $logger.error(err);
            }

            if (changeTypes.includes(CDM_POSTCODE)) {
              this._push({
                type: T_GTM,
                data: () => ({
                  event: 'receipt_method_added',
                  receipt_type: receiptType,
                  location: destination,
                  time: deliveryTime,
                }),
              });
            }
            if (changeTypes.includes(CDM_RES) && deliveryTime) {
              this._push({
                type: T_GTM,
                data: () => ({
                  event: 'eventTracker',
                  eventCat: 'Idősáv foglalás',
                  eventAct: receiptType,
                  eventLbl: destination,
                  eventValue: deliveryTime,
                }),
              });
            }
          }
        }
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_PRE_ORDER] ({
      checkoutType,
      split,
      checkoutDelivery,
    }: {
      checkoutType: string;
      split: CartSplit;
      checkoutDelivery: CheckoutData['delivery'];
    }) {
      this._push({
        type: T_GTM,
        data: () => ({
          event: `${checkoutType.toLowerCase()}_transaction`,
          transaction_value: split?.summary?.sum?.grossDiscounted,
          transaction_area: split?.packages?.[0]?.discriminator?.companyNo,
          transaction_zipcode: checkoutDelivery?.address?.address?.postCode || checkoutDelivery?.departmentStore?.name,
        }),
      });
    },
    async [PUSH_POST_ORDER] ({
      user,
      split,
      orderIdent,
    }: {
      user: UserType;
      split: CartSplit;
      orderIdent: string;
    }) {
      try {
        const {
          shipping: shippingPrice,
        } = getAnalyticsPricesFromSplit({ split });
        try {
          this[PUSH_TRACK_CART_STATE]({
            newCart: { splits: [split] },
            oldCart: orderStore.order,
          });
        } catch (err) {
          $logger.error(err);
        } finally {
          this[PUSH_TRACK_CART_STATE]({
            newCart: orderStore.order,
            oldCart: { splits: [split] },
          });
        }
        if ($awMergedConfig.googleTagManagerId && $awMergedConfig.isGtmTrackingEnabled) {
          const flattenItems = (split?.packages || []).flatMap(({
            shipsFrom,
            items,
          }) => {
            return (items || []).map(item => ({
              item,
              shippingDepot: shipsFrom,
            }));
          });
          const transactionShipping = shippingPrice ? `${shippingPrice} ${split.summary?.fees?.delivery?.sum?.currency}` : '-';
          this._push(
            {
              type: T_GTM,
              data: () => ({
                transactionId: orderIdent,
                transactionAffiliation: $getHost(),
                transactionTotal: split?.summary?.sum?.grossDiscounted,
                transactionTax: '0',
                transactionShipping,
                transactionProducts: flattenItems.map(({ item }) => {
                  return {
                    sku: item.variant?.sku ?? '',
                    name: item.variant?.name ?? '',
                    category: '-', // item.variant?.,old code had some categoryForGTM and fallback for '-'
                    price: item.variant?.price?.gross ?? 0,
                    quantity: item.quantity?.quantity ?? 0,
                  };
                }),
              }),
            },
            {
              // https://developers.google.com/tag-manager/enhanced-ecommerce#purchases
              // Clear the previous ecommerce object.
              type: T_GTM,
              data: () => ({
                ecommerce: null,
              }),
            },
          );
          try {
            // TODO backenddel és mobillal egyeztetni, hogy kell-e még ez a végpont és ha nem, akkor ki lehet írtani backendről a végpontot
            // const analytics = await $api.$get(`/orders/${orderIdent}/analytics`);
            this._push({
              type: T_GTM,
              data: () => ({
                ecommerce: {
                  purchase: {
                    actionField: {
                      id: orderIdent,
                      affiliation: $getHost(),
                      revenue: `${split?.summary?.sum?.grossDiscounted} ${split?.summary?.sum?.currency}`,
                      tax: '0',
                      shipping: transactionShipping,
                      coupon: '',
                    },
                    products: flattenItems.map(({ item }) => {
                      return {
                        name: item.variant?.name ?? '',
                        id: item.variant?.sku ?? '',
                        price: item.variant?.price?.gross ?? 0,
                        brand: item.variant?.brandName || '',
                        category: item.categories?.slice?.(-1)?.[0]?.name || '',
                        variant: item.variant?.name ?? '',
                        quantity: item.quantity?.quantity ?? 0,
                        coupon: '',
                      };
                    }),
                  },
                },
              }),
            });
          } catch (err) {
            $logger.error(err);
          }
          try {
            this._push({
              type: T_GTM,
              data: () => {
                const products = flattenItems.map(({
                  shippingDepot,
                  item,
                }) => {
                  try {
                    return new AnalyticsOrderCartItem({
                      checkoutType: split?.checkoutType,
                      cartItem: item,
                      shippingDepot: shippingDepot ?? '',
                    });
                  } catch (err) {
                    $logger.error(err);
                    return null;
                  }
                }).filter((item: AnalyticsOrderCartItem | null): item is AnalyticsOrderCartItem => item !== null);
                return {
                  event: 'order_confirmation',
                  order_id: orderIdent,
                  userID: user?.personalInfo?.cdEncodedExternalId,
                  ...getAnalyticsPricesFromSplit({ split }),
                  products_quantity: products.reduce((a, p) => a + (p?.quantity || 0), 0),
                  products,
                };
              },
            });
          } catch (err) {
            $logger.error(err);
          }
        }
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_PURCHASE] ({
      user,
      split,
      orderIdent,
      delivery,
    }: {
      user: UserType;
      split: CartSplit;
      orderIdent: string;
      delivery: CheckoutData['delivery'];
    }) {
      try {
        const purchasedItems = (split?.packages?.flatMap?.(({ items }) => items || []) || []).map((product) => {
          return new AnalyticsProduct({
            product,
            variantProperty: 'variant',
            checkoutType: split.checkoutType,
          });
        });
        const address = delivery?.address?.address || delivery?.departmentStore?.address;

        const isPickupPoint = delivery?.pickupPointData?.id;
        this._push({
          type: T_GTM,
          data: () => {
            return {
              event: 'cleanup',
              ecommerce: null,
            };
          },
        });
        this._push({
          type: T_GTM,
          data: () => {
            return {
              event: 'purchase',
              first_name: user.personalInfo?.firstName,
              last_name: user.personalInfo?.lastName,
              email: user.personalInfo?.email,
              phone: delivery?.address?.contactPhone,
              country: 'HU',
              postal_code: (isPickupPoint ? delivery?.pickupPointData : address)?.postCode,
              city: (isPickupPoint ? delivery?.pickupPointData : address)?.city,
              street: (isPickupPoint ? delivery?.pickupPointData?.addressLine : address?.streetName),
              number_of_products: purchasedItems.length,
              ecommerce: {
                currency: split.summary?.sum?.currency,
                value: epsRound(split.summary?.sum?.grossDiscounted ?? 0),
                tax: null,
                shipping: (split?.summary?.fees?.delivery?.sum?.grossDiscounted || 0)?.toFixed(2)?.toString(),
                affiliation: 'online.auchan.hu',
                transaction_id: orderIdent,
                coupon: split?.vouchers?.[0]?.objectId,
                items: purchasedItems,
              },
            };
          },
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_VIEW_PRODUCT_LIST] ({
      itemListName,
      searchText,
      products,
      itemListId,
    }: {
      itemListName?: string;
      searchText: string[];
      products: (ProductData | { product: ProductData; itemListId?: string })[];
      itemListId?: string;
    }) {
      try {
        const searchListName = router.currentRoute.value.path.includes('search') && 'Search';
        const resolvedListName = itemListName || searchListName || '';
        products.forEach((entry) => {
          const isWrapped = 'product' in entry;
          const product = isWrapped ? entry.product : entry;
          const resolvedItemListId = isWrapped ? entry.itemListId : itemListId;
          const item = new AnalyticsProduct({
            product,
            variantProperty: 'selectedVariant',
          });
          item.item_list_name = resolvedListName || undefined;
          item.search_term = searchText?.length ? searchText.join(',') : null;
          item.item_list_id = resolvedItemListId || undefined;
          analyticsProductListQueueStore.addToViewItemListQueue(resolvedListName, item);
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [FLUSH_VIEW_PRODUCT_LIST] () {
      try {
        const queueByList = analyticsProductListQueueStore.flushViewItemListQueue();
        queueByList.forEach((items, listName) => {
          if (!items.length) {
            return;
          }
          items.forEach((item, index) => {
            item.index = index + 1;
          });
          this._push({
            type: T_GTM,
            data: () => ({
              event: 'cleanup',
              ecommerce: null,
            }),
          });
          this._push({
            type: T_GTM,
            data: () => ({
              event: 'view_item_list',
              item_list_name: listName || undefined,
              number_of_products: items.length,
              ecommerce: {
                currency: DEFAULT_CURRENCY,
                value: getAnalyticsValueFromItems({ items }),
                items,
              },
            }),
          });
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    async [PUSH_DELETE_ORDER] ({
      user,
      orderIdent,
    }: {
      user: UserType;
      orderIdent: string;
    }) {
      try {
        if ($awMergedConfig.googleTagManagerId && $awMergedConfig.isGtmTrackingEnabled) {
          const cart = await $api.$get<DeepPartial<Schema<'CartModel'>>>(`/orders/${orderIdent}/cart`);
          const split = cart?.splits?.[0];
          const products = (split?.packages || []).flatMap(({
            shipsFrom,
            items,
          }) => {
            return (items || []).map((item) => {
              try {
                return new AnalyticsOrderCartItem({
                  checkoutType: split?.checkoutType,
                  cartItem: item,
                  shippingDepot: shipsFrom || '',
                });
              } catch (err) {
                $logger.error(err);
                return null;
              }
            }).filter((item: AnalyticsOrderCartItem | null): item is AnalyticsOrderCartItem => item !== null);
          });
          this._push({
            type: T_GTM,
            data: () => ({
              event: 'delete_order',
              order_id: orderIdent,
              userID: user?.personalInfo?.cdEncodedExternalId,
              ...getAnalyticsPricesFromSplit({ split }),
              products_quantity: products.reduce((a: number, p: AnalyticsOrderCartItem) => a + (p?.quantity || 0), 0),
              products,
            }),
          });
        }
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_VOUCHER] ({
      couponCode,
      discountsSum,
    }: {
      couponCode: string;
      discountsSum: number;
    }) {
      this._push({
        type: T_GTM,
        data: () => ({
          event: 'eventTracker',
          eventCat: 'Kupon érvényesítés',
          eventAct: couponCode,
          eventLbl: discountsSum,
        }),
      });
    },
    [PUSH_REVIEW_PRODUCT] ({
      productSku,
      numberOfStars,
    }: {
      productSku: string;
      numberOfStars: number;
    }) {
      this._push({
        type: T_GTM,
        data: () => ({
          event: 'product_rating',
          item_id: productSku,
          stars: numberOfStars,
        }),
      });
    },
    [PUSH_ZOOM_PRODUCT] () {
      this._push({
        type: T_GTM,
        data: () => ({
          event: 'product_zoom',
        }),
      });
    },
    [PUSH_CLICK_BANNER] ({ analyticsBannerInteraction }: { analyticsBannerInteraction: BannerInteractionInput }) {
      try {
        if (!AnalyticsBannerInteraction.isValid(analyticsBannerInteraction)) {
          $logger.error(new TypeError(`${String(analyticsBannerInteraction)} is not a valid AnalyticsBannerInteraction`));
          return;
        }
        const item = AnalyticsBannerInteraction.format(analyticsBannerInteraction);
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'cleanup',
            ecommerce: null,
          }),
        });
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'select_promotion',
            number_of_products: 1,
            ecommerce: {
              items: [item],
            },
          }),
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_VIEW_BANNER] ({ analyticsBannerInteraction }: { analyticsBannerInteraction: BannerInteractionInput }) {
      try {
        if (!AnalyticsBannerInteraction.isValid(analyticsBannerInteraction)) {
          $logger.error(new TypeError(`${String(analyticsBannerInteraction)} is not a valid AnalyticsBannerInteraction`));
          return;
        }
        const item = AnalyticsBannerInteraction.format(analyticsBannerInteraction);
        analyticsQueueStore.addToQueue(item);
      } catch (err) {
        $logger.error(err);
      }
    },
    [FLUSH_VIEW_BANNER] ({ isHistoryFlushed }: { isHistoryFlushed?: boolean } = {}) {
      try {
        const items = analyticsQueueStore.getQueue();
        analyticsQueueStore.clearQueue();
        if (isHistoryFlushed) {
          analyticsQueueStore.clearQueueHistory();
        }
        if (items.length) {
          this._push({
            type: T_GTM,
            data: () => ({
              event: 'cleanup',
              ecommerce: null,
            }),
          });
          this._push({
            type: T_GTM,
            data: () => ({
              event: 'view_promotion',
              number_of_products: items.length,
              ecommerce: {
                items,
              },
            }),
          });
        }
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_NEWSLETTER] () {
      this._push({
        type: T_GTM,
        data: () => ({
          event: 'eventTracker',
          eventCat: 'Hírlevél feliratkozás',
          eventAct: $getHref(),
          eventLbl: '',
        }),
      });
    },
    [PUSH_REGISTRATION_SUCCESS] () {
      this._push({
        type: T_GTM,
        data: () => ({
          event: 'eventTracker',
          eventCat: 'Regisztráció',
          eventAct: 'Sikeres',
          eventLbl: '',
        }),
      });
    },
    [PUSH_REGISTRATION_ERROR] ({ error }: { error: string }) {
      this._push({
        type: T_GTM,
        data: () => ({
          event: 'eventTracker',
          eventCat: 'Regisztráció',
          eventAct: 'Hiba',
          eventLbl: error,
        }),
      });
    },
    [PUSH_LOGOUT] ({ isB2B }: { isB2B: boolean }) {
      this._push({
        type: T_GTM,
        data: () => ({
          event: 'logout',
        }),
      }, {
        // NOTE: legacy PUSH_LOGOUT event
        type: T_GTM,
        data: () => ({
          event: 'eventTracker',
          eventCat: 'Kijelentkezés',
          eventAct: new Map([
            [true, 'vállalkozás'],
            [false, 'magánszemély'],
          ]).get(isB2B),
          eventValue: $getHref(),
        }),
      });
    },
    async [PUSH_LOGIN_SUCCESS] () {
      try {
        const userData = userStore.data;
        const {
          value: sha512CdExternalId,
          error: hashingErr,
        } = await crossPlatformHashNotExceptionSafe({
          hashFunction: HASH_SHA_512,
          hashableStr: userData?.personalInfo?.cdExternalId ?? '',
        });
        if (hashingErr) {
          $logger.error(hashingErr);
        }
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'login',
            user_id: userData?.personalInfo?.cdExternalId, // user id
            user_id_sha512: sha512CdExternalId, // user id sha512 hash
            environment: $awMergedConfig.isGtmRunningInProduction ? 'prod' : 'dev', // prod|dev
            direct_id: userData?.id, // Direct id, integer. Ha nem elérhető, akkor ne küldjük.
            // direct_name: 'auchan_plock', // Direct name. Ha nem elérhető, akkor ne küldjük.
            user_logged: 'yes', // Konstans ennél az event
            user_age: userData?.personalInfo?.age, // Látogató életkora, integer
            user_gender: ({
              1: 'male',
              2: 'female',
            })[Number(userData?.personalInfo?.gender)], // Látogató neme
            user_category: 'client', // Konstans ennél az eventnél
            // service_name: 'hipermarket', // hipermarket|supermarket, azaz store type. Ha nem elérhető, akkor ne küldjük
            country: 'Hungary', // Konstans ennél az eventnél
          }),
        }, {
          // NOTE: legacy PUSH_LOGIN_SUCCESS event
          type: T_GTM,
          data: () => ({
            event: 'eventTracker',
            eventCat: 'Bejelentkezés',
            eventAct: 'Sikeres',
            eventLbl: '',
          }),
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_LOGIN_ERROR] ({ error }: { error: string }) {
      this._push({
        type: T_GTM,
        data: () => ({
          event: 'eventTracker',
          eventCat: 'Bejelentkezés',
          eventAct: 'Hiba',
          eventLbl: error,
        }),
      });
    },
    [PUSH_RANGE_FILTER] ({
      value0,
      value1,
    }: {
      value0: number;
      value1: number;
    }) {
      this._push({
        type: T_GTM,
        data: () => ({
          event: 'eventTracker',
          eventCat: 'Szűrés',
          eventAct: 'Ár',
          eventLbl: `${value0}-${value1}`,
        }),
      });
    },
    [PUSH_VIEW_CART] ({ splits }: { splits: CartSplit[] }) {
      try {
        const items = splits.flatMap(s => (s.packages || []).flatMap(p => p.items || []).map(item => ({
          item,
          checkoutType: s.checkoutType,
        }))).map(({ item, checkoutType }) => {
          const currentItem = new AnalyticsProduct({
            product: item,
            variantProperty: 'variant',
            checkoutType,
          });
          currentItem.quantity = Math.round(itemToQuantity<string>(item));
          return currentItem;
        });
        if (!items.length) {
          return;
        }
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'cleanup',
            ecommerce: null,
          }),
        });
        this._push({
          type: T_GTM,
          data: () => ({
            event: 'view_cart',
            number_of_products: items.length,
            ecommerce: {
              currency: splits.find(s => s?.summary?.sum?.currency)?.summary?.sum?.currency || DEFAULT_CURRENCY,
              value: epsRound(splits.reduce((sum, s) => sum + (s?.summary?.sum?.grossDiscounted ?? 0), 0)),
              items,
            },
          }),
        });
      } catch (err) {
        $logger.error(err);
      }
    },
    [PUSH_SELECT_HEADER_ITEM] ({ headerName, headerId }: { headerName: string; headerId: string }) {
      this._push({
        type: T_GTM,
        data: () => ({
          event: 'fejlec_adatok',
          fejlec_name: headerName,
          fejlec_id: headerId,
        }),
      });
    },
  };

  if (import.meta.client) {
    awAnalytics[LISTEN_FOR_SEO_CHANGE]();
  }
  return {
    awAnalytics,
  };
}

export type AwAnalyticsPluginProvides = ReturnType<typeof createPluginProvides>;

export default defineNuxtPlugin(() => {
  return {
    provide: createPluginProvides(),
  };
});

export function deliveryMethodsEnumToDeliveryMethod (deliveryMethodEnumElement: typeof deliveryMethodsEnum[keyof typeof deliveryMethodsEnum]) {
  return {
    [deliveryMethodsEnum.HOME_DELIVERY]: DM_DELIVERY_ADDRESS,
    [deliveryMethodsEnum.PICK_UP_FROM_STORE]: DM_DELIVERY_CLICK_AND_COLLECT,
  }[deliveryMethodEnumElement];
}

export function genClickEventHandlers (eventCallback: (...args: unknown[]) => void) {
  return {
    click () {
      eventCallback(...arguments);
    },
    auxclick () {
      eventCallback(...arguments);
    },
    contextmenu () {
      eventCallback(...arguments);
    },
  };
}
